import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ShieldCheck, Info, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import useModalA11y from "@/lib/useModalA11y";

/*
 * ConsentGate — global GDPR consent management.
 *
 * Wrap the whole app in <ConsentProvider>. Anywhere you're about to process
 * personal health information (record a video, upload a file, DM a clinician)
 * call `const { ensureConsent } = useConsent()` then `await ensureConsent('health_data')`.
 * If the user has already granted the kind, resolves immediately. Otherwise a
 * blocking modal explains what will be collected + why. Consent is persisted
 * on the user profile server-side via POST /api/consents/{kind}.
 */

const CONSENT_COPY = {
  health_data: {
    title: "Health data processing",
    subtitle: "Before you can send a message to your clinician",
    what: [
      "Direct messages between you and your clinician",
      "Any pain / mood / fatigue scores you include in a message",
      "Timestamps + read receipts so your clinician knows when you replied",
    ],
    why: "Your clinician can only adjust your rehab plan when they can see what you're feeling day-to-day. All communications are treated as clinical records and kept as long as your active care plan requires (see Privacy Policy).",
  },
  video_recording: {
    title: "In-session video recording",
    subtitle: "Before you record yourself doing an exercise",
    what: [
      "Short video of you performing the exercise + audio",
      "The exercise + set the clip was recorded during",
      "Automatic delivery to your assigned clinician",
    ],
    why: "Your clinician needs to see your form to spot compensations that pain scores alone won't reveal. Clips are stored encrypted and only visible to you and your assigned clinician.",
  },
  ai_coach: {
    title: "AI Recovery Coach",
    subtitle: "Before the coach can suggest changes to your plan",
    what: [
      "The text of your coach conversations",
      "The pain / mood / fatigue you enter during check-ins",
      "Metadata about which suggestions were approved by your clinician",
    ],
    why: "The coach uses AI to flag safety risks and draft rehab tweaks. Every suggestion is reviewed by a human clinician before it reaches your plan. We never send your name, email, or NHS number to the AI provider.",
  },
  wearables: {
    title: "Wearable device sync",
    subtitle: "Before we can read data from Apple Health / WHOOP / Garmin",
    what: [
      "Sleep, heart-rate, HRV, steps and workout summaries",
      "Only the aggregated daily snapshot — never raw GPS or continuous biometrics",
    ],
    why: "Wearable data helps your clinician detect over-training or poor recovery early. You can disconnect a device at any time and its historical data will be deleted within 30 days.",
  },
};

const ConsentCtx = createContext({
  ensureConsent: async () => true,
  refresh: () => {},
});

export function useConsent() {
  return useContext(ConsentCtx);
}

export function ConsentProvider({ children }) {
  const { user } = useAuth();
  const [consents, setConsents] = useState({});
  const [prompt, setPrompt] = useState(null); // { kind, resolve, reject }

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/consents");
      setConsents(data?.consents || {});
    } catch { /* transient — leave existing map */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const ensureConsent = useCallback(async (kind) => {
    if (!user) return false;
    if (consents[kind]?.granted) return true;
    // If we haven't loaded yet, do one refresh before prompting.
    try {
      const fresh = await api.get("/consents").then((r) => r.data?.consents || {});
      setConsents(fresh);
      if (fresh[kind]?.granted) return true;
    } catch { /* fall through to prompt */ }
    return new Promise((resolve) => {
      setPrompt({ kind, resolve });
    });
  }, [consents, user]);

  const grant = async () => {
    if (!prompt) return;
    try {
      await api.post(`/consents/${prompt.kind}`, { granted: true, version: "1.0" });
      setConsents((c) => ({ ...c, [prompt.kind]: { granted: true, version: "1.0", at: new Date().toISOString() } }));
      prompt.resolve(true);
      setPrompt(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record consent. Try again.");
    }
  };

  const decline = () => {
    if (!prompt) return;
    prompt.resolve(false);
    setPrompt(null);
    toast.info("No worries — you can grant this consent anytime from Security Settings.");
  };

  const copy = prompt ? (CONSENT_COPY[prompt.kind] || CONSENT_COPY.health_data) : null;
  const consentDialogRef = useModalA11y({ open: !!prompt, onClose: decline });

  const value = useMemo(() => ({ ensureConsent, refresh, consents }), [ensureConsent, refresh, consents]);

  return (
    <ConsentCtx.Provider value={value}>
      {children}
      {prompt && copy && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="consent-modal">
          <div
            ref={consentDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-title"
            className="fl-card p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                   style={{ background: "color-mix(in oklab, var(--fl-orange) 18%, transparent)", color: "var(--fl-orange)" }}>
                <ShieldCheck size={18} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[#f47a20] font-bold">Consent needed</div>
                <h2 id="consent-title" className="fl-heading text-xl mt-1">{copy.title}</h2>
                <p className="text-xs text-zinc-500 mt-1">{copy.subtitle}</p>
              </div>
              <button onClick={decline} className="text-zinc-500 hover:text-white" aria-label="Close" data-testid="consent-close">
                <X size={16} />
              </button>
            </div>
            <div className="text-sm text-zinc-300 space-y-3">
              <div>
                <div className="fl-label mb-1 flex items-center gap-1.5"><Info size={12} className="text-[#f47a20]" /> What we will collect</div>
                <ul className="list-disc list-inside space-y-0.5 marker:text-[#f47a20] text-[13px]">
                  {copy.what.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </div>
              <div>
                <div className="fl-label mb-1 flex items-center gap-1.5"><Info size={12} className="text-[#f47a20]" /> Why</div>
                <p className="text-[13px] leading-relaxed">{copy.why}</p>
              </div>
              <p className="text-[11px] text-zinc-500 pt-1">
                You can withdraw this consent at any time from Security Settings. See our
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#f47a20] hover:underline mx-1">Privacy Policy</a>
                for the full picture.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={decline} className="text-sm text-zinc-400 hover:text-white" data-testid="consent-decline">
                Not now
              </button>
              <button onClick={grant} className="fl-btn" data-testid="consent-grant">
                <ShieldCheck size={14} /> I understand — grant consent
              </button>
            </div>
          </div>
        </div>
      )}
    </ConsentCtx.Provider>
  );
}
